import React, { CSSProperties, useState } from 'react';
import { IconsList } from 'src/components/BeaconV2Components/EntityTableRefresh/EntityTableHeader';
import Flex from 'src/components/BeaconV2Components/Flex/Flex';
import Pagination from 'src/components/BeaconV2Components/Pagination/Pagination';
import ProductCell from 'src/components/BeaconV2Components/ProductCell/ProductCell';
import AtlasV2ProductListContainer from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2ProductListContainer';
import { usePaginatedProductData } from 'src/components/AtlasV2Components/utils/tableUtils';
import {
  DorisGroupByFieldNameEnum,
  DorisMetricEndPointEnum,
  DorisMetricTypeEnum
} from 'src/components/AtlasV2Components/Consts/DorisFieldNameEnum';
import {
  BrandColumnDefinition,
  CategoryColumnDefinition,
  SubcategoryColumnDefinition
} from 'src/components/BeaconV2Components/MetricList/headColumnDefinitions';
import { useMainEntityType } from 'src/utils/Hooks/reduxSelectors';
import { GridSortOptions } from 'src/components/AtlasV2Components/AtlasV2graphs/AtlasV2MetricListContainer';
import { getAtlasSalesV2Treatment } from 'src/utils/app';
import AtlasSalesExportModal from 'src/components/AtlasV2Components/AtlasDorisExportComponents/AtlasSalesExportModal';

enum DropdownOption {
  Product = DorisGroupByFieldNameEnum.Product,
  Category = DorisGroupByFieldNameEnum.Category,
  Subcategory = DorisGroupByFieldNameEnum.Subcategory,
  Brand = DorisGroupByFieldNameEnum.Brand
}

const fields = [
  {
    name: 'retailSales',
    displayName: 'Retail Sales',
    wrapperStyle: {
      width: '93px'
    }
  },
  {
    name: 'retailPrice',
    displayName: 'Retail Price',
    wrapperStyle: {
      width: '93px'
    }
  },
  {
    name: 'unitsSold',
    displayName: 'Units Sold',
    wrapperStyle: {
      width: '93px'
    }
  }
];

const dropdownOptionToColumnDefinition = {
  [DropdownOption.Product]: {
    headerName: 'Products',
    valueFormatter: (_, row: { product: any }) => (
      <div style={{ width: '580px' }}>
        <ProductCell
          key={row?.product?.stacklineSku}
          stacklineSku={row?.product?.stacklineSku}
          title={row?.product?.title}
        />
      </div>
    )
  },
  [DropdownOption.Category]: CategoryColumnDefinition,
  [DropdownOption.Subcategory]: SubcategoryColumnDefinition,
  [DropdownOption.Brand]: BrandColumnDefinition
};

export interface ProductMetadata {
  stacklineSku: string;
  title: string;
  retailerSku: string;
  imageUrl?: string;
  [key: string]: any;
}

const dropdownOptionToDisplayName = {
  [DropdownOption.Product]: 'Product',
  [DropdownOption.Category]: 'Category',
  [DropdownOption.Subcategory]: 'Subcategory',
  [DropdownOption.Brand]: 'Brand'
};

export const AtlasKeyMetricsGridV2 = ({ salesType = 'retailSales' }: { salesType: string }) => {
  const [dropdownOption, setDropdownOption] = useState<DropdownOption>(DropdownOption.Product);
  const [sortOption, setSortOption] = useState<GridSortOptions>(GridSortOptions.TopSelling);
  const mainEntityType = useMainEntityType();

  const {
    enrichedData,
    isLoading,
    isMetadataLoading,
    currentPage,
    handleNextPage,
    handlePreviousPage,
    getPagedValuesByFieldName,
    getPagedValueDifferencesByFieldName,
    setCurrentPage,
    totalFilteredCount,
    ITEMS_PER_PAGE
  } = usePaginatedProductData({
    apiMethodName: DorisMetricEndPointEnum.SalesEndPoint,
    groupByFieldName: dropdownOption,
    metricType:
      dropdownOption === DropdownOption.Product
        ? DorisMetricTypeEnum.ProductsWithSalesData
        : DorisMetricTypeEnum.SalesSummary,
    additionalParams: {
      sortby: DorisMetricTypeEnum.RetailSales
    },
    fields,
    sortFieldName: salesType,
    sortOption: sortOption
  });

  // Reset page when sort option changes
  React.useEffect(() => {
    setCurrentPage(1);
  }, [sortOption, setCurrentPage]);

  // Reset sort option when dropdown option changes
  React.useEffect(() => {
    setSortOption(GridSortOptions.TopSelling);
    setCurrentPage(1);
  }, [dropdownOption, setCurrentPage]);

  const dropdownOptionsByEntityType = {
    category: [DropdownOption.Product, DropdownOption.Subcategory, DropdownOption.Brand],
    subcategory: [DropdownOption.Product, DropdownOption.Brand]
  };

  // Calculate if we should show pagination based on filtered count
  const hasMorePages =
    totalFilteredCount > 0 ? totalFilteredCount > currentPage * ITEMS_PER_PAGE : enrichedData.length >= ITEMS_PER_PAGE;

  return (
    <Flex flexDirection="column" marginTop={'80px'} gap={16}>
      <AtlasV2ProductListContainer
        showGrid={dropdownOption === DropdownOption.Product}
        indexName="sales"
        mainFieldName={salesType}
        showSortOptions
        sortOption={sortOption}
        onSortOptionChange={setSortOption}
        fields={fields}
        entityTableHeaderProps={{
          prefixTitle: 'Retail Sales by',
          customExportModal: (props: { open: boolean; close: () => void }) => (
            <AtlasSalesExportModal
              open={props.open}
              onClose={props.close}
              metricType={DorisMetricTypeEnum.RetailSales}
            />
          ),
          title: '',
          groupByFields: (
            dropdownOptionsByEntityType[mainEntityType] || [
              DropdownOption.Product,
              DropdownOption.Category,
              DropdownOption.Subcategory,
              DropdownOption.Brand
            ]
          ).map((option) => ({
            name: option,
            displayName: dropdownOptionToDisplayName[option]
          })),
          handleGroupByChange: (event) => setDropdownOption(event.target.value as DropdownOption)
        }}
        headColumnDefinition={dropdownOptionToColumnDefinition[dropdownOption]}
        customDataSource={{
          data: enrichedData,
          isLoading: isLoading || isMetadataLoading,
          getValuesByFieldName: getPagedValuesByFieldName,
          getValueDifferencesByFieldName: getPagedValueDifferencesByFieldName,
          totalFilteredCount: totalFilteredCount
        }}
        useTableDataProps={{
          itemsPerPage: 20 // This will fetch 1000 items (20 * 50) in useDorisTableData
        }}
        shouldHidePagination
        iconList={[IconsList.export, IconsList.tile, IconsList.table]}
      />
      <Flex justifyContent="flex-end">
        <Pagination
          currentPage={currentPage}
          shouldUseInfinitePagination
          onClickNext={hasMorePages ? handleNextPage : undefined}
          capPage={50}
          onClickPrevious={handlePreviousPage}
        />
      </Flex>
    </Flex>
  );
};
